import { FormGroup, Validators } from '@angular/forms';

/**
 * Declarative conditional-validator registry for the add-connection dialog.
 *
 * Replaces the two hand-written `configureObjectStorageValidators` /
 * `configureServiceValidators` methods (each ~50 lines of clear-then-set) with
 * a single generic engine plus per-form rule tables. A rule says: "when these
 * trigger controls hold these values, mark these controls required." Pure and
 * unit-testable against plain FormGroups.
 */

/** When every `when` control holds one of the listed values, `require` controls become required. */
export interface ConditionalRequiredRule {
  when: Record<string, string[]>;
  require: string[];
}

export interface ConditionalValidatorConfig {
  /** Controls the engine manages — all are cleared before rules are re-applied. */
  managedControls: string[];
  rules: ConditionalRequiredRule[];
}

/**
 * ObjectStorage: required fields depend on the provider (which credentials the
 * provider needs) and the auth type (which secret/credential to require).
 */
export const OBJECT_STORAGE_VALIDATORS: ConditionalValidatorConfig = {
  managedControls: ['endpoint', 'region', 'projectId', 'accountName', 'accessKey', 'secretId', 'userCredentialId', 'storageGrantId'],
  rules: [
    { when: { provider: ['S3'] }, require: ['endpoint', 'region'] },
    { when: { provider: ['AWS'] }, require: ['region'] },
    // GCS has no provider-specific requirement: bucket names are global, and
    // nothing reads a connection's project ID, so it's optional (EHQ-427).
    { when: { provider: ['Azure'] }, require: ['accountName'] },
    { when: { authType: ['AccessKey'] }, require: ['accessKey', 'secretId'] },
    { when: { authType: ['ServiceAccount'] }, require: ['secretId'] },
    { when: { authType: ['OAuth'] }, require: ['userCredentialId'] },
    // Keyless: no credential, but an approved storage grant (EHQ-427).
    { when: { authType: ['ServerIdentity'] }, require: ['storageGrantId'] },
  ],
};

/**
 * Service: a global service only needs its id; a custom service needs a url and,
 * per its auth type, a secret plus an optional header/param name.
 */
export const SERVICE_VALIDATORS: ConditionalValidatorConfig = {
  managedControls: ['globalServiceId', 'url', 'authSecretId', 'authHeaderName', 'authParamName'],
  rules: [
    { when: { mode: ['global'] }, require: ['globalServiceId'] },
    { when: { mode: ['custom'] }, require: ['url'] },
    { when: { mode: ['custom'], authType: ['bearer'] }, require: ['authSecretId'] },
    { when: { mode: ['custom'], authType: ['customHeader'] }, require: ['authSecretId', 'authHeaderName'] },
    { when: { mode: ['custom'], authType: ['queryParam'] }, require: ['authSecretId', 'authParamName'] },
  ],
};

function ruleMatches(form: FormGroup, rule: ConditionalRequiredRule): boolean {
  return Object.entries(rule.when).every(([control, values]) => values.includes(form.get(control)?.value));
}

/**
 * Clears the validators on every managed control, then re-applies
 * `Validators.required` to the controls named by matching rules. Mirrors the
 * original clear-then-set methods, including the load-bearing
 * `updateValueAndValidity({ emitEvent: false })` that prevents the
 * `valueChanges` re-entrancy the manual code carefully avoided.
 */
export function applyConditionalValidators(form: FormGroup, config: ConditionalValidatorConfig): void {
  for (const name of config.managedControls) {
    form.get(name)?.clearValidators();
  }

  const required = new Set<string>();
  for (const rule of config.rules) {
    if (ruleMatches(form, rule)) {
      rule.require.forEach((name) => required.add(name));
    }
  }
  for (const name of required) {
    form.get(name)?.setValidators([Validators.required]);
  }

  for (const name of config.managedControls) {
    form.get(name)?.updateValueAndValidity({ emitEvent: false });
  }
}
